import {CollisionEventType, Component, ForceMode, Object3D, PhysXComponent, property, Type} from '@wonderlandengine/api';
import { vec3 } from 'gl-matrix';
import Logger from './utils/logger.js';
import { vectorProjection } from './utils/utils.js';
import { PlayerMovementHandler } from './player-movement-handler.js';

const MODULE = 'WASDPhysxControls';
const _direction = new Float32Array(3);

/**
 * wasd-physx-controls
 */
class WasdPhysxControlsComponent extends Component {
    static TypeName = 'wasd-physx-controls';
    
    @property.bool()
    activeInXR: boolean = false;

    @property.bool()
    activeOutXR: boolean = false;

    @property.bool()
    lockY: boolean = false;

    @property.float(2000)
    acceleration: number = 2000.0;

    @property.float(2.5)
    maxSpeed: number = 2.5;

    @property.object()
    playerObject: Object3D | null = null; //must have a player-movement-handler component

    @property.object()
    headObject: Object3D | null = null;

    private up: boolean = false;
    private right: boolean = false
    private down: boolean = false;
    private left: boolean = false;
    private physxComp: PhysXComponent | null = null;
    private inXR = false;
    private _playerMovementHandler: PlayerMovementHandler | null = null;

    init() {
        this.up = false;
        this.right = false;
        this.down = false;
        this.left = false;
        this.physxComp = this.object.getComponent('physx') as PhysXComponent;
        this._playerMovementHandler = this.playerObject?.getComponent(PlayerMovementHandler) as PlayerMovementHandler;

        this.engine.onXRSessionStart.add(this.onXRSessionStart.bind(this));
        this.engine.onXRSessionEnd.add(this.onXRSessionEnd.bind(this));
    }

    onXRSessionStart (session : XRSession) {
        this.inXR = true;
        if (this.activeInXR) {
            window.addEventListener('keydown', this.press.bind(this));
            window.addEventListener('keyup', this.release.bind(this));
        } else {
            window.removeEventListener('keydown', this.press.bind(this));
            window.removeEventListener('keyup', this.release.bind(this));
        }
    }

    onXRSessionEnd () {
        this.inXR = false;
        if (this.activeOutXR) {
            window.addEventListener('keydown', this.press.bind(this));
            window.addEventListener('keyup', this.release.bind(this));       
        } else {
            window.removeEventListener('keydown', this.press.bind(this));
            window.removeEventListener('keyup', this.release.bind(this));        
        }
    }

    start() {
        this.headObject = this.headObject || this.object;
        if (this.activeOutXR) {
            window.addEventListener('keydown', this.press.bind(this));
            window.addEventListener('keyup', this.release.bind(this));
        }
    }

    update(delta: number) {
        if ((this.inXR && this.activeInXR) || (!this.inXR && this.activeOutXR)) {
            vec3.zero(_direction);

            let deltaAccel = this.acceleration * delta;

            if (!this.up && !this.down && !this.left && !this.right) {
                return;
            }
            if (this.up)
                _direction[2] -= 1.0;
            if (this.down)
                _direction[2] += 1.0;
            if (this.left)
                _direction[0] -= 1.0;
            if (this.right)
                _direction[0] += 1.0;
            vec3.normalize(_direction, _direction);
            _direction[0] *= deltaAccel;
            _direction[2] *= deltaAccel;
            this.headObject?.transformVectorWorld(_direction);
            if (this.lockY) {
                _direction[1] = 0;
                vec3.normalize(_direction, _direction);
                vec3.scale(_direction, _direction, deltaAccel);
            }

            // Limit speed. Should only apply to wasd movement, not other forces (like jump)
            // Only limit horizontal speed (x,z plane), allow unlimited vertical speed (y-axis)
            let currentSpeed = 0;
            if (this.physxComp) {
                // Measure speed only in x,z plane (horizontal)
                const horizontalVelocity = vec3.fromValues(
                    this.physxComp.linearVelocity[0],
                    0,
                    this.physxComp.linearVelocity[2]
                );
                currentSpeed = vec3.length(horizontalVelocity);
                if (Math.abs(currentSpeed) > this.maxSpeed) {
                    // Subtract only the horizontal component to preserve vertical movement
                    vec3.subtract(_direction, _direction, vectorProjection(_direction, horizontalVelocity));
                }
            }

            this._playerMovementHandler?.requestMovement(_direction);
        }
    }

    press(e: { keyCode: number; }) {
        if (e.keyCode === 38 /* up */ ||
            e.keyCode === 87 /* w */ ||
            e.keyCode === 90 /* z */) {
            this.up = true;
        }
        else if (e.keyCode === 39 /* right */ || e.keyCode === 68 /* d */) {
            this.right = true;
        }
        else if (e.keyCode === 40 /* down */ || e.keyCode === 83 /* s */) {
            this.down = true;
        }
        else if (e.keyCode === 37 /* left */ ||
            e.keyCode === 65 /* a */ ||
            e.keyCode === 81 /* q */) {
            this.left = true;
        } else if (e.keyCode === 32 /* space */) {
            Logger.debug("Jump requested by space", MODULE);
            this._playerMovementHandler?.requestJump();
        }
    }
    release(e: { keyCode: number; }) {
        if (e.keyCode === 38 /* up */ ||
            e.keyCode === 87 /* w */ ||
            e.keyCode === 90 /* z */) {
            this.up = false;
        }
        else if (e.keyCode === 39 /* right */ || e.keyCode === 68 /* d */) {
            this.right = false;
        }
        else if (e.keyCode === 40 /* down */ || e.keyCode === 83 /* s */) {
            this.down = false;
        }
        else if (e.keyCode === 37 /* left */ ||
            e.keyCode === 65 /* a */ ||
            e.keyCode === 81 /* q */) {
            this.left = false;
        }
    }
}
export { WasdPhysxControlsComponent };
